import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AuthContainer from './AuthContainer';
import HomePage from './pages/home';
import AcademyPage from './pages/academy/AcademyPage';
import DocLibrary from './pages/academy/DocLibrary';
import MarketPage from './pages/market/MarketPage';
import DocsPage from './pages/DocsPage';
import RebatePage from './pages/RebatePage';
import RebateDetailPage from './pages/RebateDetailPage';
import CommunityPage from './pages/CommunityPage';
import MarketHeatmapPage from './pages/MarketHeatmapPage';
import AdminPage from './pages/AdminPage';
import PatternMatchPage from './features/patternMatch/PatternMatchPage';
import DumpDetectivePage from './features/dumpDetective/DumpDetectivePage';
import DumpDetectiveReportPage from './features/dumpDetective/DumpDetectiveReportPage';
import SupportResistancePage from './features/supportResistance/SupportResistancePage';
import UserProfile from './components/UserProfile';
import CryptoDetailPage from './pages/CryptoDetailPage';
import InstitutionDetailPage from './pages/InstitutionDetailPage';
import InstitutionListPage from './pages/InstitutionListPage';
import StockFundamentalsPage from './pages/StockFundamentalsPage';
import StockDetailPage from './pages/StockDetailPage';
import BooksPage from './pages/books/BooksPage';
import BookDetailPage from './pages/books/BookDetailPage';
import GlobalNav from './components/GlobalNav';
import { LanguageProvider } from './i18n/context';
import { ThemeProvider } from './context/ThemeContext';
import useUserStore from './store/userStore';
import { ROUTES } from './routes/routeConfig';

function ProtectedRoute({ isLoggedIn, children }) {
  return isLoggedIn ? children : <Navigate to={ROUTES.login} />;
}

function AppInner() {
  const { isLoggedIn, token } = useUserStore();
  const location = useLocation();
  const hideNav = location.pathname === ROUTES.home
    || location.pathname.startsWith('/market')
    || location.pathname.startsWith('/login')
    || location.pathname.startsWith('/register')
    || location.pathname.startsWith('/forgot')
    || location.pathname.startsWith('/admin');

  // 页面刷新时校验 token 恢复登录
  useEffect(() => {
    if (token) {
      const store = useUserStore.getState();
      store.verifySession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!hideNav && <GlobalNav />}
      <Routes>
        <Route path={ROUTES.home} element={<HomePage />} />

        <Route path={ROUTES.academy} element={<AcademyPage />} />
        <Route path={ROUTES.academyBooks} element={<BooksPage />} />
        <Route path={ROUTES.academyArticles} element={<DocLibrary />} />
        <Route path={ROUTES.academyGuides} element={<DocLibrary />} />
        <Route path={ROUTES.academyKnowledge} element={<Navigate to={ROUTES.academy} replace />} />
        <Route path={ROUTES.academyQuotes} element={<Navigate to={ROUTES.academy} replace />} />
        <Route path={ROUTES.academyKnowledgeDetail} element={<DocsPage />} />

        <Route path={ROUTES.market} element={<MarketPage />} />
        <Route path={ROUTES.marketGold} element={<MarketPage />} />
        <Route path={ROUTES.marketStock} element={<MarketPage />} />
        <Route path={ROUTES.marketCenter} element={<Navigate to={ROUTES.market} replace />} />
        <Route path={ROUTES.marketTools} element={<Navigate to={ROUTES.market} replace />} />
        <Route path={ROUTES.marketResearch} element={<Navigate to={ROUTES.market} replace />} />
        <Route path={ROUTES.marketHeatmap} element={<MarketHeatmapPage />} />
        <Route path={ROUTES.marketToolsPatternMatch} element={<PatternMatchPage />} />
        <Route path={ROUTES.marketToolsDumpDetective} element={<DumpDetectivePage />} />
        <Route path={ROUTES.marketToolsDumpDetectiveReport} element={<DumpDetectiveReportPage />} />
        <Route path={ROUTES.marketToolsSupportResistance} element={<SupportResistancePage />} />
        <Route path={ROUTES.marketResearchInstitutions} element={<InstitutionListPage />} />
        <Route path={ROUTES.marketResearchFundamentals} element={<StockFundamentalsPage />} />

        <Route path={ROUTES.legacyDocs} element={<Navigate to={ROUTES.academy} replace />} />
        <Route path={ROUTES.legacyDocsDetail} element={<DocsPage />} />
        <Route path={ROUTES.legacyTools} element={<Navigate to={ROUTES.market} replace />} />
        <Route path={ROUTES.legacyMarketHeatmap} element={<Navigate to={ROUTES.marketHeatmap} replace />} />
        <Route path={ROUTES.legacyPatternMatch} element={<Navigate to={ROUTES.marketToolsPatternMatch} replace />} />
        <Route path={ROUTES.legacyDumpDetective} element={<Navigate to={ROUTES.marketToolsDumpDetective} replace />} />
        <Route path={ROUTES.legacyDumpDetectiveReport} element={<DumpDetectiveReportPage />} />
        <Route path={ROUTES.legacySupportResistance} element={<Navigate to={ROUTES.marketToolsSupportResistance} replace />} />
        <Route path={ROUTES.legacyInstitutions} element={<Navigate to={ROUTES.marketResearchInstitutions} replace />} />
        <Route path={ROUTES.legacyFundamentals} element={<Navigate to={ROUTES.marketResearchFundamentals} replace />} />

        <Route path={ROUTES.admin} element={<ProtectedRoute isLoggedIn={isLoggedIn}><AdminPage /></ProtectedRoute>} />
        <Route path={ROUTES.community} element={<CommunityPage />} />
        <Route path={ROUTES.rebate} element={<RebatePage />} />
        <Route path={ROUTES.rebateDetail} element={<RebateDetailPage />} />
        <Route path={ROUTES.profile} element={<ProtectedRoute isLoggedIn={isLoggedIn}><UserProfile /></ProtectedRoute>} />
        <Route path={ROUTES.cryptoDetail} element={<CryptoDetailPage />} />
        <Route path={ROUTES.bookDetail} element={<BookDetailPage />} />
        <Route path={ROUTES.institutionDetail} element={<InstitutionDetailPage />} />
        <Route path={ROUTES.stockDetail} element={<StockDetailPage />} />
        <Route path={ROUTES.login} element={isLoggedIn ? <Navigate to={ROUTES.home} /> : <AuthContainer page="login" />} />
        <Route path={ROUTES.register} element={isLoggedIn ? <Navigate to={ROUTES.home} /> : <AuthContainer page="register" />} />
        <Route path={ROUTES.forgot} element={isLoggedIn ? <Navigate to={ROUTES.home} /> : <AuthContainer page="forgot" />} />
        <Route path="*" element={<Navigate to={ROUTES.home} />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AppInner />
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
